import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ vehicleNumber: '', type: 'Bike', assignedTo: '', status: 'available' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetch = useCallback(async () => {
    try {
      const [v, s] = await Promise.all([api.get('/vehicles'), api.get('/staff')]);
      setVehicles(v.data.data);
      setStaff(s.data.data.filter(s => s.role === 'STF' && s.is_active));
    } catch { toast('Failed to load vehicles', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEditing(null); setForm({ vehicleNumber: '', type: 'Bike', assignedTo: '', status: 'available' }); setShowModal(true); };
  const openEdit = (v) => { setEditing(v); setForm({ vehicleNumber: v.vehicle_number, type: v.type, assignedTo: v.assigned_to || '', status: v.status }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.vehicleNumber) { toast('Vehicle number required', 'error'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/vehicles/${editing.id}`, form); toast('Vehicle updated!', 'success'); }
      else { await api.post('/vehicles', form); toast('Vehicle added!', 'success'); }
      setShowModal(false);
      fetch();
    } catch (err) { toast(err.response?.data?.message || 'Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this vehicle?')) return;
    try { await api.delete(`/vehicles/${id}`); toast('Vehicle removed', 'success'); fetch(); }
    catch { toast('Failed to delete', 'error'); }
  };

  const typeIcon = { Bike: '🏍️', Van: '🚐', Truck: '🚛', Car: '🚗' };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🚚 Fleet Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{vehicles.length} vehicles registered</p>
        </div>
        <button id="add-vehicle-btn" className="btn btn-primary" onClick={openAdd}>+ Add Vehicle</button>
      </div>

      {vehicles.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">🚗</div><p>No vehicles yet</p><button className="btn btn-primary" onClick={openAdd}>+ Add Vehicle</button></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {vehicles.map(v => (
            <div key={v.id} className="card" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontSize: 36 }}>{typeIcon[v.type] || '🚗'}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openEdit(v)}>✏️</button>
                  <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(v.id)}>🗑️</button>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 17, fontWeight: 700 }}>{v.vehicle_number}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{v.type}</div>
              </div>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div>
                  {v.assigned_staff_name ? (
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Assigned to</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{v.assigned_staff_name}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Unassigned</div>
                  )}
                </div>
                <span className={`badge ${v.status === 'available' ? 'badge-delivered' : v.status === 'in_use' ? 'badge-dispatched' : 'badge-cancelled'}`}>
                  {v.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="form-grid">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Vehicle Number *</label>
                  <input className="form-input" placeholder="e.g. TN01AB1234" value={form.vehicleNumber} onChange={e => setForm(p => ({ ...p, vehicleNumber: e.target.value.toUpperCase() }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    {['Bike', 'Van', 'Truck', 'Car'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign To Staff</label>
                  <select className="form-select" value={form.assignedTo} onChange={e => setForm(p => ({ ...p, assignedTo: e.target.value }))}>
                    <option value="">None</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.username}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    <option value="available">Available</option>
                    <option value="in_use">In Use</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer" style={{ margin: 0, padding: 0, border: 'none', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Vehicle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;
